import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Section, FinalCTA } from "@/components/v2/blocks";
import HowSteps from "@/components/v2/HowSteps";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import { Card, CardContent } from "@/components/ui/card";
import Seo from "@/components/v2/Seo";
import {
  Activity, Users, Cpu, Clock, Building2, Gauge,
  CalendarClock, FileText, Settings2, TrendingUp,
  RefreshCw, Network, Mail, ArrowRight,
} from "lucide-react";

const PLUS_LOIN = [
  { to: "/professionnels/anticipation-des-contrats", label: "Anticipation des contrats", sub: "Professionnels", icon: RefreshCw },
  { to: "/professionnels/gestion-multi-sites", label: "Gestion multi-sites", sub: "Professionnels", icon: Network },
  { to: "/contact", label: "Nous contacter", sub: "Étude gratuite", icon: Mail },
];

const FACTEURS = [
  { icon: Activity, title: "Votre activité", text: "Un changement d'activité modifie vos besoins énergétiques." },
  { icon: Users, title: "Vos effectifs", text: "Plus d'équipes, c'est une consommation qui évolue." },
  { icon: Cpu, title: "Vos équipements", text: "De nouvelles machines changent votre profil de consommation." },
  { icon: Clock, title: "Vos horaires", text: "Des plages d'activité différentes impactent votre contrat." },
  { icon: Building2, title: "Votre organisation", text: "Une réorganisation interne peut justifier une révision." },
  { icon: Gauge, title: "Votre consommation", text: "Le volume consommé évolue avec votre entreprise." },
];

const AU_DELA = [
  { icon: CalendarClock, title: "Les échéances", text: "Anticiper les dates clés de vos contrats." },
  { icon: FileText, title: "Les caractéristiques du contrat", text: "Conditions, options et clauses à connaître." },
  { icon: Settings2, title: "Les données techniques", text: "Puissance souscrite, profil et points de livraison." },
  { icon: TrendingUp, title: "Les évolutions de l'entreprise", text: "Adapter le contrat à votre trajectoire." },
];

const PROCESS = [
  { title: "Vous nous contactez", text: "Vous demandez une étude ou transmettez vos contrats." },
  { title: "Nous analysons", text: "Nous étudions vos contrats et votre consommation." },
  { title: "Nous comparons", text: "Nous identifions les pistes d'optimisation adaptées." },
  { title: "Nous vous accompagnons", text: "Nous vous guidons jusqu'à la décision, sans pression." },
];

export default function OptimisationContrats() {
  const facteursRef = useRef(null);
  const [facteursShown, setFacteursShown] = useState(false);

  useEffect(() => {
    const el = facteursRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setFacteursShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div>
      <Seo
        title="Optimisation des contrats d'énergie pour les professionnels"
        description="Energy Market analyse vos contrats d'électricité et de gaz pour vérifier leur adéquation avec vos besoins réels. Étude gratuite, sans engagement, partout en France."
        path="/professionnels/optimisation-des-contrats"
      />

      <PageHeroPhoto
        cmsKey="optimisation"
        eyebrow="Professionnels · Optimisation"
        title="Optimisez vos contrats d'énergie professionnels"
        subtitle="Les besoins énergétiques d'une entreprise évoluent constamment. Un contrat adapté il y a quelques années ne l'est plus forcément aujourd'hui. Energy Market analyse vos contrats pour vérifier leur adéquation avec vos besoins actuels."
        image="/img/opti.png"
        overlay="brand-light"
        flipLogo
        highlightsLabel="Ce que nous étudions"
        highlights={[
          "L'adéquation de vos contrats avec vos besoins réels",
          "Les échéances et caractéristiques à anticiper",
          "Les données techniques souvent négligées",
          "Les pistes d'optimisation, au-delà du prix",
        ]}
      />

      {/* POURQUOI ANALYSER */}
      <Section
        cmsKey="optimisation.pourquoi"
        eyebrow="Pourquoi analyser son contrat ?"
        title="Votre entreprise change, votre contrat aussi"
        intro="Plusieurs éléments peuvent évoluer et justifier une analyse régulière de votre situation énergétique."
      >
        <div className="grid items-stretch gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Visuel */}
          <div className="relative min-h-[18rem] lg:min-h-0">
            <div className="h-full overflow-hidden rounded-3xl ring-1 ring-border shadow-xl">
              <picture>
                <source srcSet="/img/pourquoi-analyser.webp" type="image/webp" />
                <img
                  src="/img/pourquoi-analyser.png"
                  alt="Analyse des contrats et de la consommation d'énergie"
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </picture>
            </div>
            {/* Badge flottant */}
            <div className="absolute -bottom-5 left-5 hidden items-center gap-3 rounded-2xl bg-card/95 px-4 py-3 shadow-2xl ring-1 ring-border backdrop-blur sm:flex sm:left-auto sm:-right-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent font-display text-xl font-black text-accent-foreground">6</span>
              <div className="leading-tight">
                <p className="font-display text-sm font-bold text-primary">facteurs analysés</p>
                <p className="text-xs text-muted-foreground">pour votre entreprise</p>
              </div>
            </div>
          </div>

          {/* Facteurs compacts — recto (chiffre) / verso (info) */}
          <div ref={facteursRef} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            {FACTEURS.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className={`em-flip min-h-[9rem] ${facteursShown ? "is-flipped" : ""}`}>
                <div className="em-flip__inner" style={{ transitionDelay: `${600 + i * 420}ms` }}>
                  {/* RECTO — chiffre */}
                  <div className="em-flip__face flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card p-4 text-center">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" strokeWidth={2.1} aria-hidden />
                    </span>
                    <span className="font-display text-4xl font-black leading-none text-primary">{i + 1}</span>
                    <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{title}</span>
                  </div>
                  {/* VERSO — info */}
                  <div className="em-flip__face em-flip__back flex items-start gap-3.5 rounded-2xl border border-border bg-card p-4 shadow-lg">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                      <Icon className="h-5 w-5" strokeWidth={2.1} aria-hidden />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-bold text-primary leading-snug">{title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{text}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* AU-DELA DU PRIX */}
      <Section
        cmsKey="optimisation.audela"
        eyebrow="Au-delà du prix"
        title="Une analyse qui ne se limite pas aux tarifs"
        intro="L'optimisation prend également en compte de nombreux paramètres souvent négligés."
        muted
      >
        <div className="grid gap-6 sm:grid-cols-2">
          {AU_DELA.map(({ icon: Icon, title, text }) => (
            <Card key={title} className="rounded-2xl border-border bg-card em-card-hover">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <Icon className="h-6 w-6" strokeWidth={2.1} aria-hidden />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-primary">{title}</h3>
                  <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">{text}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* COMMENT CA FONCTIONNE */}
      <Section
        cmsKey="optimisation.how"
        eyebrow="Comment ça fonctionne ?"
        title="Un accompagnement structuré, sans engagement"
        intro="De la première prise de contact à la décision finale, chaque étape est pensée pour vous apporter clarté et sérénité."
      >
        <HowSteps steps={PROCESS} />
      </Section>

      {/* MAILLAGE */}
      <Section title="Pour aller plus loin">
        <div className="grid gap-4 sm:grid-cols-3">
          {PLUS_LOIN.map(({ to, label, sub, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:border-primary/40 hover:shadow-md"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" strokeWidth={2.1} aria-hidden />
              </span>
              <span className="flex-1">
                <span className="block text-sm font-semibold leading-snug text-primary">{label}</span>
                <span className="block text-xs text-muted-foreground">{sub}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
            </Link>
          ))}
        </div>
      </Section>

      <FinalCTA title="Demandez l'analyse gratuite de vos contrats" />
    </div>
  );
}
